/// <reference types="vite/client" />
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '../store/auth.store';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ─── Axios instance ───────────────────────────────────────────────────────────

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL as string,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Token refresh queue ──────────────────────────────────────────────────────
// Concurrent 401s from multiple in-flight requests all queue here.
// Only one refresh call is made; once resolved, all queued retries are replayed.

interface QueueItem {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}


let isRefreshing = false;
let failedQueue: QueueItem[] = [];

function processQueue(error: unknown, token: string | null): void {
  failedQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else {
      item.resolve(token!);
    }
  });
  failedQueue = [];
}

// ─── Request interceptor — attach Bearer token ────────────────────────────────

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response interceptor — silent token refresh on 401 ──────────────────────

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retried?: boolean;
    };

    // Only attempt refresh on 401, and only once per request
    if (error.response?.status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request and wait
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retried = true;
    isRefreshing = true;

    const refreshToken = useAuthStore.getState().refreshToken;

    if (!refreshToken) {
      isRefreshing = false;
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post<ApiResponse<{ accessToken: string }>>(
        `${import.meta.env.VITE_API_URL as string}/api/v1/auth/refresh`,
        { refreshToken }
      );

      if (!data.success || !data.data?.accessToken) {
        throw new Error('Refresh failed');
      }

      const newToken = data.data.accessToken;
      useAuthStore.getState().setTokens(newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      processQueue(null, newToken);
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Typed API helpers ────────────────────────────────────────────────────────
// Each helper unwraps ApiResponse<T> and returns T directly.
// Throws an Error with the API's error message on success: false.

async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const { data } = await promise;
  if (!data.success) {
    throw new Error(data.error ?? 'An unexpected error occurred');
  }
  return data.data as T;
}

export const api = {
  get: <T>(url: string, config?: Parameters<typeof axiosInstance.get>[1]) =>
    unwrap<T>(axiosInstance.get<ApiResponse<T>>(url, config)),

  post: <T>(url: string, body?: unknown, config?: Parameters<typeof axiosInstance.post>[2]) =>
    unwrap<T>(axiosInstance.post<ApiResponse<T>>(url, body, config)),

  patch: <T>(url: string, body?: unknown, config?: Parameters<typeof axiosInstance.patch>[2]) =>
    unwrap<T>(axiosInstance.patch<ApiResponse<T>>(url, body, config)),

  delete: <T>(url: string, config?: Parameters<typeof axiosInstance.delete>[1]) =>
    unwrap<T>(axiosInstance.delete<ApiResponse<T>>(url, config)),
};
